import debug from 'libraries/front-end-core/core/debug'
import { TextFragment } from 'interfaces/accordion'

// Implements the #:~:text= URL text fragment spec: https://wicg.github.io/scroll-to-text-fragment/

export const TEXT_FRAGMENT_HIGHLIGHT_CLASS = 'text-fragment-highlight'

const toPattern = (s: string): string =>
  s.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+')

const WORD_CHAR = '[\\p{L}\\p{N}]'
const leadingBoundary = (s: string): string => (new RegExp(`^${WORD_CHAR}`, 'u').test(s.trim()) ? `(?<!${WORD_CHAR})` : '')
const trailingBoundary = (s: string): string => (new RegExp(`${WORD_CHAR}$`, 'u').test(s.trim()) ? `(?!${WORD_CHAR})` : '')

const BLOCK_TAGS = /^(address|article|aside|blockquote|dd|div|dl|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|li|main|nav|ol|p|pre|section|summary|table|tbody|td|tfoot|th|thead|tr|ul)$/i

const closestBlock = (node: Node): Node | null => {
  let n: Node | null = node.parentNode
  while (n != null) {
    if (n.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.test((n as Element).tagName)) return n
    n = n.parentNode
  }
  return null
}

const SKIP_TAGS = /^(script|style)$/i

const acceptNode = (node: Node): number => {
  if (node.nodeType !== Node.ELEMENT_NODE) return NodeFilter.FILTER_ACCEPT
  const tag = (node as Element).tagName
  if (SKIP_TAGS.test(tag)) return NodeFilter.FILTER_REJECT
  return tag !== 'BR' ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_ACCEPT
}

export const unmarkText = (element: Element): void => {
  element.querySelectorAll(`mark.${TEXT_FRAGMENT_HIGHLIGHT_CLASS}`).forEach(m => {
    m.parentNode?.replaceChild(document.createTextNode(m.textContent ?? ''), m)
  })
}

// DOM-walk, combine text, then map the match back to a Range - same approach as mark.js (https://markjs.io/)
export const markText = (element: Element, { start, end }: TextFragment): HTMLElement | null => {
  unmarkText(element)
  element.normalize()

  const regex = end !== undefined
    ? new RegExp(`${leadingBoundary(start)}${toPattern(start)}[\\s\\S]*?${toPattern(end)}${trailingBoundary(end)}`, 'iu')
    : new RegExp(`${leadingBoundary(start)}${toPattern(start)}${trailingBoundary(start)}`, 'iu')

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, { acceptNode })
  const nodes: Array<{ node: Text, offset: number }> = []
  let combined = ''
  let prev: Text | null = null
  let afterBr = false

  for (let n = walker.nextNode(); n != null; n = walker.nextNode()) {
    if (n.nodeType === Node.ELEMENT_NODE) { afterBr = true; continue }
    const text = n as Text
    if ((prev != null) && !/\s$/.test(combined) && !/^\s/.test(text.data)) {
      if (afterBr || closestBlock(prev) !== closestBlock(text)) combined += ' '
    }
    afterBr = false
    nodes.push({ node: text, offset: combined.length })
    combined += text.data
    prev = text
  }

  const match = combined.match(regex)
  if ((match == null) || match.index === undefined) return null

  const from = match.index
  const to = from + match[0].length

  let lo = 0
  let hi = 0
  for (let i = 0; i < nodes.length; i++) {
    if (nodes[i].offset <= from) lo = i
    if (nodes[i].offset < to) hi = i
  }

  let firstMark: HTMLElement | null = null
  for (let i = lo; i <= hi; i++) {
    const { node, offset } = nodes[i]
    const segStart = i === lo ? from - offset : 0
    const segEnd = i === hi ? Math.min(to - offset, node.data.length) : node.data.length
    if (segStart >= segEnd) continue
    const range = document.createRange()
    range.setStart(node, segStart)
    range.setEnd(node, segEnd)
    const mark = document.createElement('mark')
    mark.classList.add(TEXT_FRAGMENT_HIGHLIGHT_CLASS, 'bg-text-fragment-highlight', 'br1')
    try {
      range.surroundContents(mark)
      if (firstMark == null) firstMark = mark
    } catch (e) {
      debug.warn?.(`Could not mark text fragment segment: ${e instanceof Error ? e.message : String(e)}`)
    }
  }
  return firstMark
}

export const parseTextFragment = (url: string): TextFragment | null => {
  try {
    const m = url.match(/#:~:text=([^&#]*)/)
    if (m == null) return null
    const parts = m[1].split(',').map(decodeURIComponent)
    const isPrefixOrSuffix = (p: string, i: number): boolean =>
      (i === 0 && p.endsWith('-')) || (i === parts.length - 1 && p.startsWith('-'))
    const texts = parts.length > 1
      ? parts.filter((p, i) => !isPrefixOrSuffix(p, i))
      : parts
    const start = texts[0] !== undefined && texts[0] !== '' ? texts[0] : null
    const end = texts[1] !== undefined && texts[1] !== '' ? texts[1] : null
    if (start === null) return null
    return end !== null ? { start, end } : { start }
  } catch (e) {
    return null
  }
}

// Browsers strip #:~:text= from location.href before the initial page scripts run, so on a
// direct/cold navigation we fall back to the nav entry's recorded URL, captured once up front.
const navEntry = typeof performance.getEntriesByType === 'function'
  ? performance.getEntriesByType('navigation')[0]
  : null
let navEntryFragment = (navEntry != null) ? parseTextFragment(navEntry.name) : null

export const resolveTextFragment = (focusedId: string | null): TextFragment | null => {
  const parsedFragment = parseTextFragment(location.href) ?? navEntryFragment
  navEntryFragment = null
  return parsedFragment !== null && focusedId !== null && focusedId !== '' ? parsedFragment : null
}
